import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Avatar,
  CircularProgress,
  IconButton,
  Button,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';
import MobileHeader from '../components/MobileHeader';
import moment from 'moment';

const isToday = (dob) => moment(dob).format('MM-DD') === moment().format('MM-DD');

const BirthdayScreen = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [studentPage, setStudentPage] = useState(1);
  const [staffPage, setStaffPage] = useState(1);
  const [activeTab, setActiveTab] = useState(0);
  const [hasMoreStudents, setHasMoreStudents] = useState(true);
  const [hasMoreStaff, setHasMoreStaff] = useState(true);

  useEffect(() => {
    initializeScreen();
  }, []);

  const initializeScreen = async () => {
    try {
      setInitialLoading(true);
      await Promise.all([loadStudents(true), loadStaff(true)]);
    } catch (error) {
      console.error('Error initializing BirthdayScreen:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const loadStudents = async (isInitial = false) => {
    try {
      setLoading(true);
      const page = isInitial ? 1 : studentPage;
      const token = localStorage.getItem('userToken');
      const response = await api.get(
        `api/master/student/get-birthday-list?PageNumber=${page}&PageSize=10`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const newData = response.data.data || [];

      if (isInitial) {
        setStudents(newData);
        setStudentPage(2);
      } else {
        setStudents((prev) => [...prev, ...newData]);
        setStudentPage((prev) => prev + 1);
      }

      setHasMoreStudents(newData.length === 10);
    } catch (error) {
      console.error('Error loading students birthday data:', error);
      handleCatch(error);
      if (isInitial) setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStaff = async (isInitial = false) => {
    try {
      setLoading(true);
      const page = isInitial ? 1 : staffPage;
      const token = localStorage.getItem('userToken');
      const response = await api.get(
        `api/master/employee/get-birthday-list?PageNumber=${page}&PageSize=10`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const newData = response.data.data || [];

      if (isInitial) {
        setStaff(newData);
        setStaffPage(2);
      } else {
        setStaff((prev) => [...prev, ...newData]);
        setStaffPage((prev) => prev + 1);
      }

      setHasMoreStaff(newData.length === 10);
    } catch (error) {
      console.error('Error loading staff birthday data:', error);
      handleCatch(error);
      if (isInitial) setStaff([]);
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    await Promise.all([loadStudents(true), loadStaff(true)]);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const BirthdayRow = ({ item, emoji, nameKey, classLine, dobKey, last }) => {
    const today = isToday(item[dobKey]);
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            bgcolor: today ? '#E3F2FD' : '#fff',
          }}
        >
          <Box sx={{ position: 'relative', mr: 1.5, flexShrink: 0 }}>
            <Avatar
              src={
                item.photo
                  ? `data:image/png;base64,${item.photo}`
                  : item.photoPath
                  ? `https://billtronapi.hellopatna.com${item.photoPath}`
                  : undefined
              }
              sx={{ width: 48, height: 48, bgcolor: today ? '#1565C0' : colors.BLUE, fontWeight: 700 }}
            >
              {(item[nameKey] || '?')[0].toUpperCase()}
            </Avatar>
            <Box
              sx={{
                position: 'absolute',
                bottom: -4,
                right: -4,
                bgcolor: '#fff',
                borderRadius: '50%',
                fontSize: 14,
                width: 22,
                height: 22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }}
            >
              {emoji}
            </Box>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography sx={{ fontWeight: today ? 700 : 600, fontSize: 14, color: today ? '#1565C0' : '#222', lineHeight: 1.3 }}>
                {item[nameKey]}
              </Typography>
              {today && (
                <Box sx={{ bgcolor: '#1565C0', color: '#fff', fontSize: 10, fontWeight: 700, px: 0.75, py: 0.1, borderRadius: 1, lineHeight: 1.6, flexShrink: 0 }}>
                  TODAY
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: 12, color: '#888' }}>{classLine}</Typography>
          </Box>

          <Box sx={{
            bgcolor: today ? '#1565C0' : '#F3F4F6',
            color: today ? '#fff' : colors.BLUE,
            px: 1.25,
            py: 0.5,
            borderRadius: 2,
            flexShrink: 0,
          }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
              {moment(item[dobKey]).format('DD MMM')}
            </Typography>
          </Box>
        </Box>
        {!last && <Divider sx={{ ml: '72px' }} />}
      </>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <MobileHeader
        title="Birthdays"
        showBack={true}
        rightAction={
          <IconButton onClick={onRefresh} disabled={loading || initialLoading} sx={{ color: '#fff' }}>
            <RefreshIcon />
          </IconButton>
        }
      />

      {/* Tabs */}
      <Box sx={{ backgroundColor: '#fff', boxShadow: '0 1px 0 #eee' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 14 },
            '& .MuiTabs-indicator': { backgroundColor: colors.BLUE },
          }}
        >
          <Tab label="👨‍🎓  Students" />
          <Tab label="👨‍🏫  Staff" />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', backgroundColor: '#F5F7FA' }}>
        {initialLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
            <CircularProgress />
          </Box>
        ) : activeTab === 0 ? (
          /* ── Students ── */
          students.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 8, px: 3 }}>
              <Typography sx={{ fontSize: 52, mb: 1.5 }}>🎂</Typography>
              <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#333', mb: 1 }}>No Student Birthdays</Typography>
              <Typography sx={{ fontSize: 13, color: '#888', textAlign: 'center' }}>
                No student birthdays to celebrate right now. Check back later!
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ backgroundColor: '#fff', mt: 1, mx: 1, borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                {students.map((item, index) => (
                  <BirthdayRow
                    key={index}
                    item={item}
                    emoji="🎂"
                    nameKey="studentName"
                    classLine={`Class ${item.class}-${item.section}`}
                    dobKey="dob"
                    last={index === students.length - 1}
                  />
                ))}
              </Box>
              {hasMoreStudents && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                  <Button variant="outlined" onClick={() => loadStudents(false)} disabled={loading} sx={{ borderRadius: 20, minWidth: 160 }}>
                    {loading ? <CircularProgress size={20} /> : 'Load More'}
                  </Button>
                </Box>
              )}
            </>
          )
        ) : (
          /* ── Staff ── */
          staff.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 8, px: 3 }}>
              <Typography sx={{ fontSize: 52, mb: 1.5 }}>🎉</Typography>
              <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#333', mb: 1 }}>No Staff Birthdays</Typography>
              <Typography sx={{ fontSize: 13, color: '#888', textAlign: 'center' }}>
                No staff birthdays to celebrate right now. Check back later!
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ backgroundColor: '#fff', mt: 1, mx: 1, borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                {staff.map((item, index) => (
                  <BirthdayRow
                    key={index}
                    item={item}
                    emoji="🎉"
                    nameKey="name"
                    classLine={item.designation}
                    dobKey="dob"
                    last={index === staff.length - 1}
                  />
                ))}
              </Box>
              {hasMoreStaff && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                  <Button variant="outlined" onClick={() => loadStaff(false)} disabled={loading} sx={{ borderRadius: 20, minWidth: 160 }}>
                    {loading ? <CircularProgress size={20} /> : 'Load More'}
                  </Button>
                </Box>
              )}
            </>
          )
        )}
      </Box>
    </Box>
  );
};

export default BirthdayScreen;

